import { useState } from "react";
import {
  format,
  startOfDay,
  endOfDay,
  eachDayOfInterval,
  differenceInMinutes,
} from "date-fns";
import type { ProgramItem } from "../pages/TripDetail";

interface TimelineProps {
  items: ProgramItem[];
  trip: {
    start_date: string;
    end_date: string;
  };
}

export default function Timeline({ items, trip }: TimelineProps) {
  const [expandedDays, setExpandedDays] = useState<Record<string, boolean>>({});

  const days = eachDayOfInterval({
    start: new Date(trip.start_date),
    end: new Date(trip.end_date),
  });

  const toggleAll = (expand: boolean) => {
    const newState: Record<string, boolean> = {};
    days.forEach((d) => {
      newState[d.toISOString()] = expand;
    });
    setExpandedDays(newState);
  };

  function calculateColumns(items: ProgramItem[]) {
    const columns: ProgramItem[][] = [];
    const timedItems = items.filter((i) => !!i.start_time && !!i.end_time);

    timedItems.forEach((item) => {
      const itemStart = new Date(item.start_time!).getTime();
      let placed = false;

      for (const column of columns) {
        const last = column[column.length - 1];
        const lastEnd = new Date(last.end_time!).getTime();
        if (itemStart >= lastEnd) {
          column.push(item);
          placed = true;
          break;
        }
      }

      if (!placed) {
        columns.push([item]);
      }
    });

    const map = new Map<string, { index: number; total: number }>();
    columns.forEach((col, colIndex) => {
      col.forEach((item) => {
        map.set(item.id, { index: colIndex, total: columns.length });
      });
    });

    return map;
  }

  const categoryColors: Record<string, string> = {
    Reisen: "bg-gradient-to-br from-blue-400 to-blue-600",
    Verpflegung: "bg-gradient-to-br from-emerald-400 to-emerald-600",
    Kunst: "bg-gradient-to-br from-purple-400 to-purple-600",
    Aktivität: "bg-gradient-to-br from-orange-400 to-orange-600",
    Workshop: "bg-gradient-to-br from-pink-400 to-pink-600",
  };

  return (
    <div className="mt-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-md font-semibold">Programmübersicht</h3>
        <div className="space-x-2 text-sm">
          <button
            onClick={() => toggleAll(true)}
            className="text-blue-600 hover:underline"
          >
            Alle öffnen
          </button>
          <button
            onClick={() => toggleAll(false)}
            className="text-blue-600 hover:underline"
          >
            Alle schließen
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {days.map((day) => {
          const dayKey = day.toISOString();
          const isExpanded = expandedDays[dayKey] ?? false;
          const toggleExpanded = () =>
            setExpandedDays((prev) => ({ ...prev, [dayKey]: !isExpanded }));

          const dayStart = startOfDay(day);
          const dayEnd = endOfDay(day);
          const dayItems = items.filter((item) => {
            if (!item.start_time) return false;
            const start = new Date(item.start_time);
            return start >= dayStart && start <= dayEnd;
          });

          if (dayItems.length === 0) {
            // Kein Programmpunkt = nur Text anzeigen, kein leerer Zeitblock
            return (
              <div key={dayKey} className="border rounded overflow-hidden">
                <button
                  onClick={toggleExpanded}
                  className="w-full text-left px-4 py-2 bg-gray-100 hover:bg-gray-200 text-sm font-medium border-b"
                >
                  {format(day, "EEEE, dd.MM.yyyy")} {isExpanded ? "▲" : "▼"}
                </button>
                {isExpanded && (
                  <div className="p-6 text-center text-gray-500 italic text-sm">
                    💤 Für diesen Tag ist nichts geplant.
                  </div>
                )}
              </div>
            );
          }

          const columnMap = calculateColumns(dayItems);

          const startMin = Math.min(
            ...dayItems.map((i) => {
              const d = new Date(i.start_time!);
              return d.getHours() * 60 + d.getMinutes();
            })
          );
          const endMin = Math.max(
            ...dayItems.map((i) => {
              const d = new Date(i.end_time!);
              return d.getHours() * 60 + d.getMinutes();
            })
          );

          const totalHeight = Math.max(endMin - startMin, 60);

          return (
            <div key={dayKey} className="border rounded overflow-hidden">
              <button
                onClick={toggleExpanded}
                className="w-full text-left px-4 py-2 bg-gray-100 hover:bg-gray-200 text-sm font-medium border-b"
              >
                {format(day, "EEEE, dd.MM.yyyy")} {isExpanded ? "▲" : "▼"}
              </button>

              {isExpanded && (
                <div
                  className="relative bg-white pl-[56px] border-l-2 border-gray-300 mt-4"
                  style={{ height: `${totalHeight}px` }}
                >
                  {/* Zeitmarkierungen */}
                  {Array.from(
                    { length: Math.ceil((endMin - startMin) / 60) + 1 },
                    (_, i) => {
                      const hour = Math.floor((startMin + i * 60) / 60);
                      return (
                        <div
                          key={hour}
                          className="absolute text-gray-400 text-[10px]"
                          style={{
                            top: `${i * 60}px`,
                            left: 0,
                            width: "56px",
                          }}
                        >
                          <div className="border-t border-dashed border-gray-200 w-full" />
                          <span className="absolute left-1 -mt-2">
                            {hour.toString().padStart(2, "0")}:00
                          </span>
                        </div>
                      );
                    }
                  )}

                  {/* Programmpunkte */}
                  <div className="relative">
                    {dayItems.map((item) => {
                      const start = new Date(item.start_time!);
                      const end = new Date(item.end_time!);
                      const startMinutes =
                        start.getHours() * 60 + start.getMinutes();
                      const duration = differenceInMinutes(end, start);
                      const top = startMinutes - startMin;
                      const itemHeight = Math.max(duration, 30);
                      const col = columnMap.get(item.id);
                      const width = 100 / (col?.total ?? 1);
                      const left = (col?.index ?? 0) * width;

                      return (
                        <div
                          key={item.id}
                          className={`absolute text-white text-xs px-3 py-1 rounded-lg shadow-md cursor-default ${
                            categoryColors[item.category] || "bg-gray-400"
                          }`}
                          style={{
                            top: `${top}px`,
                            height: `${itemHeight}px`,
                            left: `calc(${left}% + 4px)`,
                            width: `calc(${width}% - 8px)`,
                          }}
                        >
                          <div className="font-semibold text-[13px] leading-snug truncate">
                            {item.title}
                          </div>
                          <div className="text-[11px]">
                            {format(start, "HH:mm")} – {format(end, "HH:mm")}
                          </div>
                          {item.description && (
                            <div className="text-[10px] italic mt-0.5 line-clamp-2">
                              {item.description}
                            </div>
                          )}
                          <div className="text-[9px] font-medium mt-1 bg-white/30 inline-block px-2 py-0.5 rounded">
                            {item.category}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
